import type { ProductWithImages, ProductWithVariants } from '@dukanti/shared';

export type ProductPublicationState = 'published' | 'draft';
export type ProductAvailabilityState = 'available' | 'outOfStock';
export type ProductCatalogFilter = 'all' | 'published' | 'draft' | 'unavailable';

export interface ProductCatalogRow {
  readonly product: ProductWithImages;
  readonly firstImageUrl: string | null;
  readonly categoryId: string;
  readonly categoryName: string;
  readonly inventory: number;
  readonly available: number;
  readonly publicationState: ProductPublicationState;
  readonly availabilityState: ProductAvailabilityState;
  readonly variantCount: number;
  readonly activeVariantCount: number;
  readonly fromPrice: boolean;
  readonly hasVariantSale: boolean;
  readonly hasDisabledVariants: boolean;
  readonly hasOutOfStockVariants: boolean;
  readonly detailPath: string;
  readonly editPath: string;
}

function hasVariantSummary(product: ProductWithImages): product is ProductWithVariants {
  return (
    'total_active_inventory' in product &&
    typeof product.total_active_inventory === 'number' &&
    'variant_count' in product &&
    typeof product.variant_count === 'number'
  );
}

export function toProductCatalogRow(product: ProductWithImages): ProductCatalogRow {
  const variantSummary = hasVariantSummary(product);
  const available = variantSummary ? product.total_active_inventory : product.inventory;
  return {
    product,
    firstImageUrl: product.images[0]?.url ?? null,
    categoryId: product.category.id,
    categoryName: product.category.name,
    inventory: available,
    available,
    publicationState: product.published ? 'published' : 'draft',
    availabilityState: available > 0 ? 'available' : 'outOfStock',
    variantCount: variantSummary ? product.variant_count : 1,
    activeVariantCount: variantSummary
      ? product.active_variant_count
      : product.inventory > 0
        ? 1
        : 0,
    fromPrice: variantSummary ? product.from_price : false,
    hasVariantSale: variantSummary ? product.has_variant_sale : product.sale_price_cents !== null,
    hasDisabledVariants: variantSummary ? product.has_disabled_variants : false,
    hasOutOfStockVariants: variantSummary
      ? product.has_out_of_stock_variants
      : product.inventory <= 0,
    detailPath: `/products/${product.id}`,
    editPath: `/products/${product.id}/edit`,
  };
}

export function matchesProductCatalogFilter(
  row: ProductCatalogRow,
  filter: ProductCatalogFilter,
): boolean {
  if (filter === 'published') return row.publicationState === 'published';
  if (filter === 'draft') return row.publicationState === 'draft';
  if (filter === 'unavailable') return row.available <= 0;
  return true;
}
